/**
 * The objektová skladba, as a screen has to reason about it (`KLQ-20260817-002`).
 *
 * **The one thing this file exists to encode: the statute fixes the bands, the architect
 * invents everything inside them.** Vyhláška 60 annex B §5 gives eight category bands and
 * nothing else. What goes in a band — `SO 101.20`, `SO 108 Bytový dom A2.1` — is written by
 * the architect as the project takes shape, per documentation level, months after the project
 * is created. The co-founder's own sticky on the ARCH_START_UP board says it plainly:
 *
 * > *"MUSIA SI JU VEDIEŤ SPRAVIŤ SAMI — ČIŽE PRIDAŤ NÁZOV A ČÍSLO, A AJ PODOBJEKTY
 * > (MAX 3 PODÚROVNE)."*
 *
 * So there is no preset list to tick anywhere in this feature. Ticking `SO 100 Príprava
 * územia` would create an object nobody asked for carrying a number nobody chose — which is
 * the same failure `docs/research/22` §1.1 caught one storey up, where a seeded
 * `Building → Floor → Zone` survived contact with real use and became litter in a real
 * project. What this file offers instead is a **suggestion the person can always overrule**.
 */

import type { ObjectKind, ProjectObject } from './api';

/**
 * The eight statutory bands. **There is no 400 band** — the numbering skips it.
 *
 * TWIN: `kalqa.domains.projects.models.SO_CATEGORIES` and the
 * `project_object_category` CHECK it renders (retro rule 12). A ninth band is a change to the
 * statute, so it arrives in a migration and here in the same sitting; the labels are catalog
 * keys (`project.objects.band.*`), never strings, because the bands are named in Slovak law
 * and read by people working in three languages.
 */
export const SO_CATEGORIES = [100, 200, 300, 500, 600, 700, 800, 900] as const;

export type SoCategory = (typeof SO_CATEGORIES)[number];

/** Both kinds, in the order the two parallel lists are always written in. */
export const OBJECT_KINDS: readonly ObjectKind[] = ['so', 'ps'] as const;

/**
 * Which band a number the architect just typed most likely belongs to — **a guess, never a
 * derivation.**
 *
 * `101.20` → `100`, `SO 705` → `700`. The rule is the leading digit times a hundred, which is
 * how the bands are constructed, and it is right nearly always. It is still only a guess for
 * one documented reason: `SO 08.1.C3` is a real folder in a real studio's tree
 * (`docs/research/22` §2–3) and maps to no band at all. A derived value would have to invent
 * one; a suggested value is cleared with one click and the row saves with `category: null`,
 * which the column allows.
 *
 * Returns `null` when nothing plausible falls out — an empty field, a `ps` (numbered on its
 * own sequence, with no bands), or a leading digit outside the eight.
 */
export function suggestCategory(kind: ObjectKind, rawNumber: string): SoCategory | null {
  if (kind !== 'so') return null;
  const digits = /^(\d)/.exec(rawNumber.trim());
  if (digits === null) return null;
  const band = Number(digits[1]) * 100;
  return SO_CATEGORIES.find((known) => known === band) ?? null;
}

/**
 * The next free annex-24 position 5 for a top-level object of this kind — `S01`, `P03`.
 *
 * Suggested for the same reason and with the same standing as the band: the architect knows
 * the number and the name, and position 5 is a portal requirement they should not have to
 * count out by hand. It is a plain field they can overwrite before saving, and after the first
 * identifier is generated from it the server stops accepting edits at all.
 *
 * **Not a derivation, and this one matters more.** `file_code` is stored rather than computed
 * precisely because deriving it from an ordinal would silently renumber every drawing already
 * filed under an object the moment somebody inserts one ahead of it. This function runs once,
 * to fill an empty input.
 */
export function suggestFileCode(kind: ObjectKind, existing: readonly ProjectObject[]): string {
  const prefix = kind === 'so' ? 'S' : 'P';
  const taken = new Set(existing.map((row) => row.file_code.toUpperCase()));
  for (let index = 1; index < 100; index += 1) {
    const candidate = `${prefix}${String(index).padStart(2, '0')}`;
    if (!taken.has(candidate)) return candidate;
  }
  // Ninety-nine objects of one kind. Hand back the empty string rather than a hundredth
  // guess: the field is required, so an empty one asks the person rather than inventing a
  // shape the annex has no form for.
  return '';
}

/**
 * A sub-object's code, below a parent that already has one — `S01` → `011`-style nesting.
 *
 * Deliberately **not** implemented, and the empty string is the honest answer. Príloha 24
 * position 6 specifies exactly one nesting shape (`011` = ČPS01.1) and it is a `ps` shape; for
 * a sub-objekt of a stavebný objekt the annex has no form at all, which is why vyhláška 60
 * § 5 ods. 2's free 45-character name exists. Guessing here would put an invented code inside
 * a published file name — so the field opens empty with the parent's code beside it, and a
 * person writes what their portal accepts.
 */
export function suggestChildFileCode(): string {
  return '';
}

/** One object with its children already attached — what a three-level list renders from. */
export interface ObjectNode {
  readonly row: ProjectObject;
  readonly children: readonly ObjectNode[];
}

/**
 * Join the flat list on `parent_id`.
 *
 * The server sends a list because a composition is tens of rows and at most three levels deep,
 * and a nested response would need a different shape per depth. This is the one grouping pass
 * that buys, and it lives here so the wizard step and the settings screen cannot disagree
 * about ordering — which is the whole of retro rule 8 for a rule this small.
 *
 * A row whose parent is missing from the list — the parent was archived and this call did not
 * ask for archived rows — is rendered at the top level rather than dropped. An object that
 * vanishes from a composition screen because of something that happened to its parent is the
 * worse of the two wrongs.
 */
export function objectTree(rows: readonly ProjectObject[]): readonly ObjectNode[] {
  const byParent = new Map<string, ProjectObject[]>();
  const present = new Set(rows.map((row) => row.id));
  const roots: ProjectObject[] = [];

  for (const row of rows) {
    // `!= null` rather than `!== null`: the field is optional on the wire, so an object with
    // no parent arrives as `undefined` from some callers and `null` from others, and only one
    // of those two spellings catches both.
    const parentId = row.parent_id;
    if (parentId != null && present.has(parentId)) {
      const siblings = byParent.get(parentId) ?? [];
      siblings.push(row);
      byParent.set(parentId, siblings);
    } else {
      roots.push(row);
    }
  }

  const build = (row: ProjectObject): ObjectNode => ({
    row,
    children: (byParent.get(row.id) ?? []).map(build),
  });
  return roots.map(build);
}

/**
 * How deep this row already sits, so a screen knows whether to offer "add a sub-object".
 *
 * The server refuses an over-deep parent with a sentence naming the limit, and that refusal is
 * the enforcement point — this is only what greys the button before somebody reaches it.
 */
export function depthOf(node: ObjectNode, from = 1): number {
  return node.children.reduce((deepest, child) => Math.max(deepest, depthOf(child, from + 1)), from);
}

/** TWIN: `kalqa.domains.projects.models.OBJECT_DEPTH_LIMIT`. Two sources, two numbers. */
export const OBJECT_DEPTH_LIMIT: Readonly<Record<ObjectKind, number>> = { so: 3, ps: 2 };

/** How an object reads in a list: `SO 108 · Bytový dom A2.1`, with the file code beside it. */
export function objectLabel(row: ProjectObject): string {
  const prefix = row.kind === 'so' ? 'SO' : 'PS';
  return `${prefix} ${row.number} · ${row.label}`;
}

/**
 * The composition as **pivot buckets** — `KLQ-20260904-004` §A, shared by every surface that
 * groups by object (drawings, tasks, reviews).
 *
 * Nested the way objects nest, and prefixed the way the drawings pivot always prefixed them:
 * a bare object id in a row id is a UUID from another table sitting where a hierarchy node id
 * is expected, and the day something looks it up as one there is nothing to notice the
 * mistake. Archived objects are absent — a drawing still filed under one answers a key no
 * bucket has, which `bucketBy` reads as *no object*, which is where it belongs.
 */
export function objectPivotGroups(
  rows: readonly ProjectObject[],
): { id: string; label: string; parentId: string | null }[] {
  const groups: { id: string; label: string; parentId: string | null }[] = [];
  const walk = (nodes: readonly ObjectNode[], parentId: string | null): void => {
    for (const node of nodes) {
      const id = objectBucketId(node.row.id);
      groups.push({ id, label: objectLabel(node.row), parentId });
      walk(node.children, id);
    }
  };
  walk(objectTree(rows), null);
  return groups;
}

/** One object's bucket id. TWIN of the prefix `objectPivotGroups` mints — one spelling. */
export function objectBucketId(objectId: string): string {
  return `object:${objectId}`;
}
